/**
 * PHENAPRIN BRAND AUTO-STYLING
 * File: phenaprin-brand.js
 * Purpose: Automatically style all instances of "PhenAprin" across the website
 * Dependencies: phenaprin-brand.css
 */

(function() {
  'use strict';

  // Configuration - Add/remove selectors as needed
  const PHENAPRIN_CONFIG = {
    excludeClasses: [
      '.no-phenaprin-styling',
      '.phenaprin-exclude'
    ],
    excludeSelectors: [
      // Add specific selectors here only if needed
    ],
    excludeAttributes: [
      '[data-no-phenaprin]'
    ]
  };

  // Auto-style PhenAprin text across the website
  function stylePhenAprinText() {
    // Function to create styled PhenAprin HTML matching CSS classes
    function createStyledPhenAprin(fullMatch, variant = '') {
      let html = '<span class="brand-phenaprin">';
      html += '<span class="phen-part">PHEN</span>';
      html += '<span class="aprin-part">APRIN</span>';
      if (variant) {
        html += '<sup class="brand-superscript">' + variant + '</sup>';
      }
      html += '</span>';
      return html;
    }

    // Get all text nodes in the document
    function getTextNodes(node) {
      let textNodes = [];
      if (node.nodeType === Node.TEXT_NODE) {
        textNodes.push(node);
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          textNodes = textNodes.concat(getTextNodes(node.childNodes[i]));
        }
      }
      return textNodes;
    }

    // Process each text node
    const textNodes = getTextNodes(document.body);
    textNodes.forEach(function(textNode) {
      let text = textNode.textContent;
      
      // Skip if already processed or if parent has brand-phenaprin class
      if (textNode.parentNode.classList && 
          (textNode.parentNode.classList.contains('brand-phenaprin') ||
           textNode.parentNode.closest('.brand-phenaprin'))) {
        return;
      }

      // Skip script and style tags
      const parentTagName = textNode.parentNode.tagName;
      if (parentTagName === 'SCRIPT' || parentTagName === 'STYLE') {
        return;
      }

      // Skip elements with exclusion classes or configuration
      const isExcluded = PHENAPRIN_CONFIG.excludeClasses.some(cls => 
        textNode.parentNode.closest(cls)
      ) || PHENAPRIN_CONFIG.excludeSelectors.some(sel => 
        textNode.parentNode.closest(sel)
      ) || PHENAPRIN_CONFIG.excludeAttributes.some(attr => 
        textNode.parentNode.closest(attr)
      );

      if (isExcluded) {
        return;
      }

      let hasReplacement = false;
      let newText = text;
      
      // Replace PhenAprin TX7 (case insensitive)
      if (/PhenAprin\s*TX7/i.test(newText)) {
        newText = newText.replace(/PhenAprin\s*TX7/gi, function(match) {
          hasReplacement = true;
          return createStyledPhenAprin(match, 'TX7');
        });
      }
      
      // Replace PhenAprin XR (case insensitive)
      if (/PhenAprin\s*XR/i.test(newText)) {
        newText = newText.replace(/PhenAprin\s*XR/gi, function(match) {
          hasReplacement = true;
          return createStyledPhenAprin(match, 'XR');
        });
      }
      
      // Replace regular PhenAprin (only if no variants were found in this text)
      if (!hasReplacement && /PhenAprin/i.test(newText)) {
        newText = newText.replace(/PhenAprin/gi, function(match) {
          hasReplacement = true;
          return createStyledPhenAprin(match);
        });
      }

      // Replace the text node with styled HTML if replacements were made
      if (hasReplacement) {
        const wrapper = document.createElement('span');
        wrapper.innerHTML = newText;
        
        // If the wrapper only contains one element, replace with that element directly
        if (wrapper.children.length === 1 && wrapper.textContent.trim() === wrapper.children[0].textContent.trim()) {
          textNode.parentNode.replaceChild(wrapper.children[0], textNode);
        } else {
          textNode.parentNode.replaceChild(wrapper, textNode);
        }
      }
    });
  }

  // Debounce function to limit how often we run the styling
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = function() {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Debounced version of the styling function
  const debouncedStylePhenAprin = debounce(stylePhenAprinText, 100);

  // Run the function when the page loads
  document.addEventListener('DOMContentLoaded', stylePhenAprinText);

  // Also run after Shopify sections load (for theme editor and dynamic content)
  document.addEventListener('shopify:section:load', stylePhenAprinText);

  // Run after any cart updates
  document.addEventListener('cart:updated', stylePhenAprinText);

  // Run when mobile menu is toggled (common mobile menu events)
  document.addEventListener('click', function(e) {
    // Check for common mobile menu trigger elements
    if (e.target.matches('.mobile-nav-toggle') || 
        e.target.matches('.hamburger') ||
        e.target.matches('[aria-expanded]') ||
        e.target.closest('.mobile-nav-toggle') ||
        e.target.closest('.hamburger') ||
        e.target.closest('.header__icon--menu')) {
      // Run styling after a short delay to let menu animate in
      setTimeout(stylePhenAprinText, 300);
    }
  });

  // Specifically target the mobile drawer menu when it opens
  document.addEventListener('toggle', function(e) {
    // Check if it's the mobile menu details element opening
    if (e.target.matches('#Details-menu-drawer-container') ||
        e.target.closest('.menu-drawer-container') ||
        e.target.matches('[id^="Details-menu-drawer"]')) {
      // Run styling when mobile menu toggles
      setTimeout(stylePhenAprinText, 200);
    }
  });

  // Run when any drawer/modal opens (for mobile menus that use these)
  document.addEventListener('drawer:open', stylePhenAprinText);
  document.addEventListener('modal:open', stylePhenAprinText);

  // For any other dynamic content (quick views, AJAX loaded content, etc.)
  const observer = new MutationObserver(function(mutations) {
    let shouldProcess = false;
    
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        // Check if any added nodes contain text that might have PhenAprin
        for (let node of mutation.addedNodes) {
          if (node.nodeType === Node.TEXT_NODE && /PhenAprin/i.test(node.textContent)) {
            shouldProcess = true;
            break;
          } else if (node.nodeType === Node.ELEMENT_NODE && 
                     node.textContent && /PhenAprin/i.test(node.textContent)) {
            shouldProcess = true;
            break;
          }
        }
      }
      
      // Also check for attribute changes (like class or style changes that might show hidden content)
      if (mutation.type === 'attributes' && 
          (mutation.attributeName === 'class' || 
           mutation.attributeName === 'style' ||
           mutation.attributeName === 'aria-expanded')) {
        const target = mutation.target;
        if (target && target.textContent && /PhenAprin/i.test(target.textContent)) {
          shouldProcess = true;
        }
      }
    });
    
    if (shouldProcess) {
      debouncedStylePhenAprin();
    }
  });

  // Start observing for dynamic content
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'aria-expanded']
  });

  // Export for debugging purposes (optional)
  window.PhenAprinBrand = {
    stylePhenAprinText: stylePhenAprinText
  };

})();